import { Cookies } from 'react-cookie';
import { IApiService } from './IApiService';
import { BasicFetchResponse } from './ApiResponse';
import logger from '../utils/logger';
import { DateTime } from 'luxon';
import { correlationService } from './CorrelationService';

export interface RetryConfig {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
  backoffMultiplier: number;
  retryableStatusCodes: number[];
}

// Helper function to get the API base URL from environment
function getApiBaseUrl(): string {
  // In Vite environment, import.meta.env is available
  // In test environment, we use the default
  return import.meta.env.VITE_APP_API_BASE_URL || 'http://localhost:3000/api';
}

export class ApiService implements IApiService {
  private baseUrl: string;
  private cookies: Cookies;
  private retryConfig: RetryConfig;

  constructor(baseUrl: string = getApiBaseUrl(), cookies: Cookies = new Cookies(), retryConfig?: Partial<RetryConfig>) {
    this.baseUrl = baseUrl;
    this.cookies = cookies;
    this.retryConfig = {
      maxRetries: 3,
      initialDelayMs: 1000,
      maxDelayMs: 10000,
      backoffMultiplier: 2,
      retryableStatusCodes: [408, 429, 500, 502, 503, 504],
      ...retryConfig,
    };
  }

  public async post(url: string, data: any): Promise<BasicFetchResponse> {
    return this.requestWithRetry(this.normalizeUrl(url), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  public async get(url: string): Promise<BasicFetchResponse> {
    return this.requestWithRetry(this.normalizeUrl(url), {
      method: 'GET',
    });
  }

  public async delete(url: string): Promise<BasicFetchResponse> {
    return this.requestWithRetry(this.normalizeUrl(url), {
      method: 'DELETE',
    });
  }

  public async put(url: string, data: any): Promise<BasicFetchResponse> {
    return this.requestWithRetry(this.normalizeUrl(url), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
  }

  public async putFormData(url: string, formData: FormData): Promise<BasicFetchResponse> {
    return this.requestWithRetry(this.normalizeUrl(url), {
      method: 'PUT',
      body: formData,
    });
  }

  private normalizeUrl(url: string) {
    if (url[0] !== '/') url = `/${url}`;
    return url;
  }

  private async sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private calculateBackoffDelay(attempt: number): number {
    const delay = this.retryConfig.initialDelayMs * Math.pow(this.retryConfig.backoffMultiplier, attempt);
    return Math.min(delay, this.retryConfig.maxDelayMs);
  }

  private shouldRetry(statusCode: number, attempt: number): boolean {
    return attempt < this.retryConfig.maxRetries && this.retryConfig.retryableStatusCodes.includes(statusCode);
  }

  private async requestWithRetry(url: string, options: RequestInit): Promise<BasicFetchResponse> {
    let lastError: Error | null = null;
    let lastResponse: BasicFetchResponse | null = null;

    for (let attempt = 0; attempt <= this.retryConfig.maxRetries; attempt++) {
      try {
        const response = await this.request(url, options);

        // If successful or non-retryable error, return immediately
        if (response.status < 400 || !this.shouldRetry(response.status, attempt)) {
          return response;
        }

        // Store response for potential retry
        lastResponse = response;

        // Calculate backoff delay
        if (attempt < this.retryConfig.maxRetries) {
          const delay = this.calculateBackoffDelay(attempt);
          logger.warn(`Retrying request after ${delay}ms`, {
            url,
            method: options.method,
            attempt: attempt + 1,
            maxRetries: this.retryConfig.maxRetries,
            statusCode: response.status,
          });
          await this.sleep(delay);
        }
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // For network errors, retry
        if (attempt < this.retryConfig.maxRetries) {
          const delay = this.calculateBackoffDelay(attempt);
          logger.warn(`Retrying request after network error (${delay}ms)`, {
            url,
            method: options.method,
            attempt: attempt + 1,
            maxRetries: this.retryConfig.maxRetries,
            error: lastError.message,
          });
          await this.sleep(delay);
        }
      }
    }

    // If we exhausted retries, throw or return last response
    if (lastError) {
      logger.error('Request failed after all retries', {
        url,
        method: options.method,
        maxRetries: this.retryConfig.maxRetries,
        error: lastError.message,
      });
      throw lastError;
    }

    if (lastResponse) {
      logger.error('Request failed after all retries', {
        url,
        method: options.method,
        maxRetries: this.retryConfig.maxRetries,
        statusCode: lastResponse.status,
      });
      return lastResponse;
    }

    // Should never reach here, but just in case
    throw new Error('Request failed with no response or error');
  }

  private async request(url: string, options: RequestInit): Promise<BasicFetchResponse> {
    const auth = this.cookies.get('auth');
    const token = auth.accessToken;
    const profile = this.cookies.get('profile');
    const userId = profile?.id;

    if (token) {
      options.credentials = 'include';
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      };
    }
    if (profile) {
      options.headers = {
        ...options.headers,
        'x-user-id': profile.id,
      };
    }
    options.cache = 'no-store';
    options.headers = {
      ...options.headers,
      'x-user-timezone': DateTime.local().zoneName,
    };

    // Add correlation token to request headers if available
    try {
      const correlationId = correlationService.get();
      const headers = new Headers(options.headers ?? {});
      // mutate headers
      headers.set('X-Correlation-ID', String(correlationId));
      options.headers = headers;
    } catch (error) {
      // Log error but continue with request
      logger.error('Failed to add correlation token to request', {
        url,
        method: options.method,
        error: error instanceof Error ? error.message : String(error),
      });
    }

    const fullUrl = this.baseUrl + url;
    const method = options.method || 'GET';

    try {
      const response = await fetch(fullUrl, options);

      // Extract correlation token from response (for both success and error responses)
      try {
        correlationService.extractFromResponse(response);
      } catch (error) {
        // Log error but continue processing response
        logger.error('Failed to extract correlation token from response', {
          url: fullUrl,
          method,
          statusCode: response.status,
          error: error instanceof Error ? error.message : String(error),
        });
      }

      const contentType = response.headers.get('Content-Type');

      logger.debug('API request completed', {
        url: fullUrl,
        method,
        statusCode: response.status,
        userId,
      });

      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        data = await response.text();
      }

      // Log errors for non-2xx responses
      if (response.status >= 400) {
        logger.error('API request failed', {
          url: fullUrl,
          method,
          statusCode: response.status,
          statusText: response.statusText,
          userId,
          responseData: data,
        });
      }

      return {
        status: response.status,
        statusText: response.statusText,
        data,
      } as BasicFetchResponse;
    } catch (error) {
      logger.logApiError('API request exception', error, {
        url: fullUrl,
        method,
      });
      throw error;
    }
  }
}
